import React, { useState } from 'react';

import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../atoms';
import { SIDEBAR_MENU_BUTTON_STYLE_OVERRIDES } from '../constants/sidebarMenuButtonStyleOverrides';

import { LanguageItems } from './components/LanguageItems';
import { useLanguageSelector } from './hooks/useLanguageSelector';
import {
  DEFAULT_ERROR_TEXT,
  DEFAULT_LABEL_DICT,
} from './LanguageSelector.config';
import { type LanguageSelectorProps } from './LanguageSelector.types';

/**
 * Language Selector Component
 *
 * A modern language selection component built with shadcn/ui that supports:
 * - Collapsed and expanded modes
 * - Customizable language options
 * - Flag icons for visual identification
 * - Accessibility support
 * - Internationalization
 *
 * @param props - Component properties
 * @returns JSX element for language selection
 *
 * @example
 * ```tsx
 * // Basic usage
 * <LanguageSelector isCollapse={false} />
 *
 * // With custom languages
 * <LanguageSelector
 *   isCollapse={true}
 *   languages={['en', 'fr']}
 *   labelDict={{
 *     'en-US': 'English (US)',
 *     en: 'English',
 *     fr: 'Français',
 *   }}
 * />
 * ```
 */
export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  isCollapse,
  labelDict = DEFAULT_LABEL_DICT,
  errorText = DEFAULT_ERROR_TEXT,
  languages,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { availableLanguages, selectedLanguageData, handleSelectLanguage } =
    useLanguageSelector({
      labelDict,
      languages,
      errorText,
    });

  // Early return if no language data is available
  if (!selectedLanguageData) {
    return null;
  }

  const handleLanguageSelect = (language: string) => {
    handleSelectLanguage(language);
    setIsOpen(false);
  };

  const { icon: Icon, label } = selectedLanguageData;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          className={SIDEBAR_MENU_BUTTON_STYLE_OVERRIDES}
          variant="ghost"
          id="language-selector-button"
          aria-label={label ?? ''}
        >
          <Icon />
          {!isCollapse && label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="dark w-auto" side="right" align="end">
        <LanguageItems
          languageData={availableLanguages}
          onSelectLanguage={handleLanguageSelect}
        />
      </PopoverContent>
    </Popover>
  );
};
